/** Presentation-only exceptions; key operations remain authorized by backend policies. */
export function hasPartnerApiSectionAccess({
  isInternalAdmin,
  email,
  workspaceId,
}: {
  isInternalAdmin: boolean;
  email?: string | null;
  workspaceId?: string | null;
}): boolean {
  return isInternalAdmin ||
    email?.toLowerCase() === "vinicius.mendes@v4company.com" ||
    workspaceId === "08578e0c-5ab7-4184-ac00-d0c548c91a52";
}

export const PARTNER_API_SCOPES = [
  { value: "leads:read", label: "Leads (leitura)", group: "CRM" },
  { value: "leads:create", label: "Leads (criar)", group: "CRM" },
  { value: "leads:update", label: "Leads (atualizar)", group: "CRM" },
  { value: "leads:write", label: "Leads (criar + atualizar)", group: "CRM" },
  { value: "leads:delete", label: "Leads (exclusão)", group: "CRM" },
  { value: "custom_fields:read", label: "Schema legado /v1/custom-fields (leitura)", group: "CRM" },
  { value: "custom_fields:write", label: "Schema legado /v1/custom-fields (escrita)", group: "CRM" },
  { value: "custom_fields:delete", label: "Schema legado /v1/custom-fields (exclusão)", group: "CRM" },
  { value: "funnels:read", label: "Funis (leitura)", group: "CRM" },
  { value: "tags:read", label: "Tags (leitura)", group: "CRM" },
  { value: "tags:write", label: "Tags (escrita)", group: "CRM" },
  { value: "tags:delete", label: "Tags (exclusão)", group: "CRM" },
  { value: "groups:read", label: "Grupos (leitura)", group: "CRM" },
  { value: "groups:write", label: "Grupos (escrita)", group: "CRM" },
  { value: "products:read", label: "Produtos (leitura)", group: "Catálogo" },
  { value: "products:write", label: "Produtos (escrita)", group: "Catálogo" },
  { value: "products:delete", label: "Produtos (exclusão)", group: "Catálogo" },
  { value: "conversations:read", label: "Conversas (leitura)", group: "Mensagens" },
  { value: "conversations:write", label: "Conversas (escrita)", group: "Mensagens" },
  { value: "messages:read", label: "Mensagens (leitura)", group: "Mensagens" },
  { value: "messages:send", label: "Mensagens (envio)", group: "Mensagens" },
  { value: "agents:read", label: "Agentes (leitura)", group: "Agentes" },
  { value: "webhooks:manage", label: "Webhooks (gestão)", group: "Sistema" },
] as const;

export type LeadCustomFieldType = "text" | "number" | "date" | "boolean" | "list";

export type LeadCustomFieldDefinition = {
  id: string;
  workspace_id?: string;
  key: string;
  label: string;
  field_type: LeadCustomFieldType;
  options: string[] | null;
  required: boolean;
  is_system: boolean;
  storage: "custom" | "metadata_root";
  order_index: number;
};

export type LeadCustomFieldRow = { id: string; key: string; value: string };

export function slugifyCustomFieldKey(raw: string): string {
  return String(raw || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 40);
}

function stringifyCustomFieldValue(raw: unknown): string {
  if (raw === undefined || raw === null) return "";
  if (typeof raw === "boolean") return raw ? "true" : "false";
  if (typeof raw === "object") return JSON.stringify(raw);
  return String(raw);
}

export function customFieldRowsFromMetadata(
  metadata: Record<string, unknown> | null | undefined,
): LeadCustomFieldRow[] {
  const meta = metadata || {};
  const nested =
    meta.custom_fields && typeof meta.custom_fields === "object" && !Array.isArray(meta.custom_fields)
      ? (meta.custom_fields as Record<string, unknown>)
      : {};
  return Object.entries(nested).map(([key, value]) => ({
    id: key,
    key,
    value: stringifyCustomFieldValue(value),
  }));
}

export function mergeCustomFieldMetadata(
  existing: Record<string, unknown> | null | undefined,
  rows: LeadCustomFieldRow[],
): Record<string, unknown> {
  const metadata: Record<string, unknown> = { ...(existing || {}) };
  const custom: Record<string, unknown> = {};

  for (const row of rows) {
    const key = slugifyCustomFieldKey(row.key);
    if (!key) continue;
    const raw = String(row.value ?? "").trim();
    if (!raw) continue;
    custom[key] = raw;
  }

  metadata.custom_fields = custom;
  return metadata;
}
